import { t } from '@/i18n'
import type { ZReport } from '@/types'

import {
  EmptySeparator,
  InfoItem,
  Label,
  Section,
  TaxesBreakdown,
  TillBreakdown,
  Total
} from './components'

function getTotalTips(report: ZReport) {
  return Object.values(report.tips).reduce((total, amount) => (total += amount), 0)
}

function getTotalOpeningFunds(report: ZReport) {
  if (!report.shifts) return 0
  return Object.values(report.shifts).reduce((sum, shift) => sum + shift.startAmount, 0)
}

function financialSummary(report: ZReport) {
  const grossSales = report.breakdown.total + report.breakdown.discounts
  const totalTips = getTotalTips(report)
  const totalCollected = report.breakdown.total + totalTips

  return [
    new Section(t('z-report.financial-summary')),
    new InfoItem(t('z-report.gross-sales'), grossSales, { valueType: 'currency' }),
    new InfoItem(t('z-report.discounts-promotions'), -report.breakdown.discounts, {
      valueType: 'currency'
    }),
    new InfoItem(t('z-report.net-sales'), report.breakdown.total, {
      valueType: 'currency',
      important: true
    }),
    new InfoItem(t('z-report.tips'), totalTips, { valueType: 'currency' }),
    new InfoItem(t('z-report.total-collected'), totalCollected, {
      valueType: 'currency',
      important: true
    })
  ]
}

function cashFlowAnalysis(report: ZReport) {
  const totalOpeningFunds = getTotalOpeningFunds(report)
  const expectedCash =
    totalOpeningFunds + (report.payments.cash ?? 0) + report.totalIns - report.totalOuts

  return [
    new Section(t('z-report.cash-flow-analysis')),
    new InfoItem(t('z-report.total-opening-funds'), totalOpeningFunds, { valueType: 'currency' }),
    new InfoItem(t('z-report.total-cash-sales'), report.payments.cash ?? 0, {
      valueType: 'currency'
    }),
    new InfoItem(t('z-report.total-pay-ins'), report.totalIns, { valueType: 'currency' }),
    new InfoItem(t('z-report.total-pay-outs'), -report.totalOuts, { valueType: 'currency' }),
    new InfoItem(t('z-report.expected-cash'), expectedCash, {
      valueType: 'currency',
      important: true
    })
  ]
}

function globalReconciliation(report: ZReport) {
  if (!report.shifts || Object.keys(report.shifts).length === 0) return []

  return [new Section(t('z-report.global-reconciliation')), new TillBreakdown(report.shifts)]
}

function taxBreakdown(report: ZReport) {
  if (Object.keys(report.breakdown.taxRates).length === 0) return []

  return [
    new Section(t('z-report.tax-breakdown')),
    new TaxesBreakdown(Object.values(report.breakdown.taxRates), report.breakdown.taxLabel!)
  ]
}

function operationalDetails(report: ZReport) {
  return [
    new Section(t('z-report.operational-details')),
    new InfoItem(t('z-report.total-tabs'), report.stats.totalTabs),
    new InfoItem(t('z-report.average-per-tab'), report.stats.averagePerTab, {
      valueType: 'currency'
    })
  ]
}

function payments(report: ZReport) {
  const sortedPayments = Object.keys(report.payments)
    .map(method => ({
      method,
      amount: report.payments[method]
    }))
    .sort((a, b) => b.amount - a.amount)
  let paymentsArray: any[] = []
  for (let i = 0; i < sortedPayments.length; ++i) {
    paymentsArray = [
      ...paymentsArray,
      new InfoItem(
        sortedPayments[i].method.charAt(0).toUpperCase() + sortedPayments[i].method.slice(1),
        sortedPayments[i].amount,
        {
          valueType: 'currency'
        }
      )
    ]
  }
  const totalPayments = Object.values(report.payments).reduce(
    (sum, amount) => sum + amount,
    0
  )
  const result = [
    new Section(t('report.payments')),
    ...paymentsArray,
    new InfoItem(t('z-report.total'), totalPayments, {
      valueType: 'currency',
      important: true
    })
  ]

  return result
}

function generateZReport(
  report: ZReport,
  config: { company: { name: string; taxId: string; address: string } }
) {
  return [
    new Label(t('report.z-report'), {
      style: 'bold 30px',
      lineHeight: 35
    }),
    new InfoItem(t('report.from'), report.startDate, {
      valueType: 'date',
      style: '16px'
    }),
    new InfoItem(t('report.to'), report.endDate, {
      valueType: 'date',
      style: '16px'
    }),
    new EmptySeparator(),
    new Label(config.company.name, { style: 'bold 24px', lineHeight: 30 }),
    new Label(config.company.taxId, { style: '16px', lineHeight: 30 }),
    new Label(config.company.address, { style: '16px', lineHeight: 30 }),
    ...financialSummary(report),
    ...payments(report),
    ...cashFlowAnalysis(report),
    ...globalReconciliation(report),
    ...taxBreakdown(report),
    ...operationalDetails(report),
    new EmptySeparator(),
    new Total(report.breakdown.total, null, getTotalTips(report) > 0 ? 'with-tips' : null)
  ]
}

export default generateZReport
